import React, { useEffect, useRef, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { mediaURL } from 'src/consumer/services'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { QuillWrapper } from '../helperComponentsStyles'

/**
 * Rich Text Editor using TipTap (replacement for ReactQuill)
 * Supports image upload and full formatting toolbar
 */
export default function RichTextEditorQuill({
  id,
  rawId,
  htmlContent,
  initialContent,
  handleChangeHtml = () => {},
  handleChangeRaw = () => {},
  toolbarOptionsOverride,
  disabled,
}) {
  const editorRef = useRef(null)
  const isInternalUpdate = useRef(false)
  const fileInputRef = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start typing...',
      }),
    ],
    content: htmlContent || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true
      const html = editor.getHTML()
      const cleanedHtml = html === '<p></p>' ? '' : html
      const quillEvent = { target: { value: cleanedHtml, id } }
      handleChangeHtml(quillEvent)
      setTimeout(() => {
        isInternalUpdate.current = false
      }, 0)
    },
    editorProps: {
      attributes: {
        class: 'tiptap-quill-editor',
      },
    },
    autofocus: false,
  })

  // Store editor ref
  useEffect(() => {
    if (editor) {
      editorRef.current = editor
    }
  }, [editor])

  // Update editor content when htmlContent prop changes (external updates only)
  useEffect(() => {
    if (editor && htmlContent !== undefined && !isInternalUpdate.current) {
      const currentContent = editor.getHTML()
      const normalizedCurrent = currentContent === '<p></p>' ? '' : currentContent
      const normalizedValue = htmlContent || ''
      if (normalizedCurrent !== normalizedValue) {
        editor.commands.setContent(normalizedValue, false)
      }
    }
  }, [editor, htmlContent])

  // Update editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [editor, disabled])

  // Image upload handler
  const handleImageUpload = useCallback(async (file) => {
    if (!file || !editor) return

    if (!/^image\//.test(file.type)) {
      console.warn('You could only upload images.')
      return
    }

    const fd = new FormData()
    fd.append('file', file)

    try {
      const resp = await postDataWithoutParams('/api/media/file/upload', fd)
      if (resp.status === 200) {
        const imageUrl = `${mediaURL}${resp?.data?.fileId}`
        editor.chain().focus().setImage({ src: imageUrl }).run()
      }
    } catch (error) {
      console.error('Image upload failed:', error)
    }
  }, [editor])

  // Trigger file input for image upload
  const selectLocalImage = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  // Handle file input change
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleImageUpload(file)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleImageUpload])

  if (!editor) {
    return null
  }

  return (
    <QuillWrapper>
      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />

      {/* Toolbar */}
      <div className="tiptap-quill-toolbar">
        <select
          className="toolbar-select"
          onChange={(e) => {
            const level = parseInt(e.target.value)
            if (level === 0) {
              editor.chain().focus().setParagraph().run()
            } else {
              editor.chain().focus().toggleHeading({ level }).run()
            }
          }}
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' :
            editor.isActive('heading', { level: 4 }) ? '4' :
            editor.isActive('heading', { level: 5 }) ? '5' :
            editor.isActive('heading', { level: 6 }) ? '6' : '0'
          }
        >
          <option value="0">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
          <option value="5">Heading 5</option>
          <option value="6">Heading 6</option>
        </select>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
          disabled={disabled}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
          disabled={disabled}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
          disabled={disabled}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
          disabled={disabled}
        >
          <s>S</s>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          title="Quote"
          disabled={disabled}
        >
          "
        </button>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Numbered List"
          disabled={disabled}
        >
          1.
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
          disabled={disabled}
        >
          •
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:')
            if (url) {
              editor.chain().focus().setLink({ href: url }).run()
            }
          }}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Link"
          disabled={disabled}
        >
          🔗
        </button>
        <button
          type="button"
          onClick={selectLocalImage}
          title="Insert Image"
          disabled={disabled}
        >
          🖼️
        </button>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Clear Formatting"
          disabled={disabled}
        >
          ✕
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} style={{ height: '100%' }} />

      <style dangerouslySetInnerHTML={{
        __html: `
          .tiptap-quill-toolbar {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 8px 12px;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
            flex-wrap: wrap;
          }
          .tiptap-quill-toolbar .toolbar-select {
            padding: 4px 8px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            background: #fff;
            font-size: 13px;
            color: #64748b;
            cursor: pointer;
          }
          .tiptap-quill-toolbar button {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px 8px;
            border: none;
            background: transparent;
            border-radius: 4px;
            cursor: pointer;
            font-size: 13px;
            color: #64748b;
            transition: all 0.15s;
            min-width: 28px;
            height: 28px;
          }
          .tiptap-quill-toolbar button:hover:not(:disabled) {
            background: #e2e8f0;
            color: #1e293b;
          }
          .tiptap-quill-toolbar button.is-active {
            background: #6779e3;
            color: #fff;
          }
          .tiptap-quill-toolbar button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .tiptap-quill-toolbar .toolbar-divider {
            width: 1px;
            height: 20px;
            background: #e2e8f0;
            margin: 0 4px;
          }
          .tiptap-quill-editor {
            outline: none;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #1f2937;
            padding: 16px;
            min-height: 400px;
            height: calc(100% - 50px);
            overflow-y: auto;
          }
          .tiptap-quill-editor p {
            margin: 0.5em 0;
          }
          .tiptap-quill-editor p:first-child {
            margin-top: 0;
          }
          .tiptap-quill-editor p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: #adb5bd;
            pointer-events: none;
            height: 0;
          }
          .tiptap-quill-editor h1,
          .tiptap-quill-editor h2,
          .tiptap-quill-editor h3,
          .tiptap-quill-editor h4,
          .tiptap-quill-editor h5,
          .tiptap-quill-editor h6 {
            font-weight: 600;
            margin: 0.5em 0;
          }
          .tiptap-quill-editor h1 { font-size: 2em; }
          .tiptap-quill-editor h2 { font-size: 1.5em; }
          .tiptap-quill-editor h3 { font-size: 1.17em; }
          .tiptap-quill-editor h4 { font-size: 1em; }
          .tiptap-quill-editor h5 { font-size: 0.83em; }
          .tiptap-quill-editor h6 { font-size: 0.67em; }
          .tiptap-quill-editor .tiptap-link {
            color: #6779e3;
            text-decoration: underline;
          }
          .tiptap-quill-editor .tiptap-image {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 1em 0;
            border-radius: 4px;
          }
          .tiptap-quill-editor ul,
          .tiptap-quill-editor ol {
            padding-left: 1.5em;
            margin: 0.5em 0;
          }
          .tiptap-quill-editor blockquote {
            border-left: 3px solid #6779e3;
            padding-left: 1em;
            margin: 0.5em 0;
            color: #64748b;
            font-style: italic;
          }
        `
      }} />
    </QuillWrapper>
  )
}
